import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CloudSyncRounded from '@mui/icons-material/CloudSyncRounded';
import DevicesRounded from '@mui/icons-material/DevicesRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import ImageRounded from '@mui/icons-material/ImageRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import TimerRounded from '@mui/icons-material/TimerRounded';
import WarningRounded from '@mui/icons-material/WarningRounded';
import {
  Box,
  Button,
  Card,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { api, unwrap } from '../../api/client';
import type { PlatformHealth } from '../../api/m19';
import { EmptyState, ErrorState, LoadingRows, PageHeader } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { Field, IconBadge, Panel, StatGrid, StatTile, type Tone } from '../../components/ui';
import { bytes, count, dateTime, duration, relativeTime } from '../../lib/format';

export const HEALTH_REFRESH_MS = 30_000;

type Overall = 'HEALTHY' | 'DEGRADED' | 'DOWN';

/** Overall status and the reasons behind it (thresholds are console-side judgement calls). */
export function assess(h: PlatformHealth): { status: Overall; reasons: string[] } {
  const reasons: string[] = [];
  if (!h.database.ok) return { status: 'DOWN', reasons: ['The database is not answering.'] };
  if (h.database.latencyMs !== null && h.database.latencyMs > 250)
    reasons.push(`Database latency is high (${String(Math.round(h.database.latencyMs))} ms).`);
  if (h.sync.failedCommands24h > 0)
    reasons.push(`${count(h.sync.failedCommands24h)} sync commands failed in 24 h.`);
  if (h.sync.openConflicts > 0)
    reasons.push(`${count(h.sync.openConflicts)} sync conflicts are waiting for review.`);
  if (h.sync.staleDevices.length > 0)
    reasons.push(`${count(h.sync.staleDevices.length)} devices have not synced for over 3 days.`);
  return { status: reasons.length ? 'DEGRADED' : 'HEALTHY', reasons };
}

export function ms(v: number | null): string {
  if (v === null) return '—';
  return `${v < 10 ? v.toFixed(1) : String(Math.round(v))} ms`;
}

function latencyTone(ms: number | null): Tone {
  if (ms === null) return 'neutral';
  if (ms > 250) return 'error';
  if (ms > 80) return 'warning';
  return 'success';
}

/** Re-renders every few seconds so "updated 20 s ago" stays true. */
function useNow(intervalMs = 5_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

function ago(at: number, now: number): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  return s < 5 ? 'just now' : `${duration(s)} ago`;
}

export function HealthPage() {
  const query = useQuery({
    queryKey: ['platform-health'],
    queryFn: () => unwrap(api.GET('/platform/health')),
    refetchInterval: HEALTH_REFRESH_MS,
    refetchIntervalInBackground: false,
  });
  const now = useNow();
  const h = query.data;

  return (
    <Box>
      <PageHeader
        eyebrow="Platform"
        title="System health"
        subtitle="API, database, sync and file storage at a glance. Refreshes every 30 seconds."
        actions={
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            {query.dataUpdatedAt > 0 && (
              <Stack
                direction="row"
                spacing={0.75}
                sx={{ alignItems: 'center' }}
                aria-live="polite"
                data-testid="refreshed-at"
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: query.isError ? 'error.main' : 'success.main',
                    boxShadow: (t) =>
                      `0 0 0 3px color-mix(in srgb, ${(t.vars ?? t).palette.success.main} 18%, transparent)`,
                  }}
                />
                <Typography variant="body2" color="text.secondary" className="tabular">
                  {query.isFetching ? 'Refreshing…' : `Updated ${ago(query.dataUpdatedAt, now)}`}
                </Typography>
              </Stack>
            )}
            <Button
              variant="outlined"
              startIcon={<RefreshRounded />}
              onClick={() => void query.refetch()}
              loading={query.isFetching}
            >
              Refresh
            </Button>
          </Stack>
        }
      />

      {query.isError && !h ? (
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      ) : !h ? (
        <>
          <StatGrid>
            {['API uptime', 'Database latency', 'Active devices', 'Sync commands'].map((l) => (
              <StatTile key={l} label={l} value="" loading />
            ))}
          </StatGrid>
          <Panel>
            <LoadingRows rows={5} />
          </Panel>
        </>
      ) : (
        <HealthBody h={h} />
      )}
    </Box>
  );
}

function HealthBody({ h }: { h: PlatformHealth }) {
  const { status, reasons } = assess(h);
  const failRate = h.sync.commands24h > 0 ? h.sync.failedCommands24h / h.sync.commands24h : 0;
  return (
    <>
      <OverallBanner status={status} reasons={reasons} />
      <StatGrid>
        <StatTile
          label="API uptime"
          value={duration(h.api.uptimeSeconds)}
          hint={`since ${dateTime(h.api.startedAt)}`}
          icon={<TimerRounded />}
        />
        <StatTile
          label="Database latency"
          value={ms(h.database.latencyMs)}
          hint={h.database.ok ? 'Connected' : 'Not answering'}
          tone={h.database.ok ? latencyTone(h.database.latencyMs) : 'error'}
          icon={<StorageRounded />}
        />
        <StatTile
          label="Active devices"
          value={count(h.sync.devicesActive24h)}
          hint="Synced in the last 24 h"
          tone="info"
          icon={<DevicesRounded />}
        />
        <StatTile
          label="Sync commands"
          value={count(h.sync.commands24h)}
          hint={`${count(h.sync.failedCommands24h)} failed in 24 h`}
          tone={h.sync.failedCommands24h > 0 ? 'warning' : 'success'}
          icon={<CloudSyncRounded />}
        />
      </StatGrid>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: 'repeat(4, 1fr)' },
          mb: 2.5,
        }}
      >
        <Panel title="API" subtitle="This server process">
          <FieldGrid>
            <Field label="Version">
              <Mono>{h.api.version}</Mono>
            </Field>
            <Field label="Node.js">
              <Mono>{h.api.nodeVersion}</Mono>
            </Field>
            <Field label="Started">{dateTime(h.api.startedAt)}</Field>
            <Field label="Uptime">{duration(h.api.uptimeSeconds)}</Field>
          </FieldGrid>
        </Panel>
        <Panel
          title="Database"
          subtitle="PostgreSQL"
          action={
            <StatusChip
              status={h.database.ok ? 'HEALTHY' : 'DOWN'}
              label={h.database.ok ? 'OK' : 'Down'}
            />
          }
        >
          <FieldGrid>
            <Field label="Latency">{ms(h.database.latencyMs)}</Field>
            <Field label="Size">{bytes(h.database.sizeBytes)}</Field>
            <Field label="Migrations">
              {h.database.migrations ? count(h.database.migrations.applied) : '—'}
            </Field>
            <Field label="Latest migration">
              {h.database.migrations?.latest ? <Mono>{h.database.migrations.latest}</Mono> : '—'}
            </Field>
          </FieldGrid>
        </Panel>
        <Panel title="Sync" subtitle="Last 24 hours">
          <FieldGrid>
            <Field label="Commands">{count(h.sync.commands24h)}</Field>
            <Field label="Failed">
              <Box
                component="span"
                sx={{ color: h.sync.failedCommands24h > 0 ? 'warning.main' : undefined }}
              >
                {count(h.sync.failedCommands24h)}
                {h.sync.commands24h > 0 && ` (${(failRate * 100).toFixed(1)}%)`}
              </Box>
            </Field>
            <Field label="Open conflicts">
              <Box
                component="span"
                sx={{ color: h.sync.openConflicts > 0 ? 'warning.main' : undefined }}
              >
                {count(h.sync.openConflicts)}
              </Box>
            </Field>
            <Field label="Active devices">{count(h.sync.devicesActive24h)}</Field>
          </FieldGrid>
        </Panel>
        <Panel title="Files" subtitle="Stored uploads">
          <FieldGrid>
            <Field label="Images">{count(h.files.images)}</Field>
            <Field label="Image bytes">{bytes(h.files.bytes)}</Field>
          </FieldGrid>
          <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: 'center' }}>
            <IconBadge icon={<ImageRounded />} size={28} tone="neutral" />
            <Typography variant="caption" color="text.secondary">
              Products, logos and photos
            </Typography>
          </Stack>
        </Panel>
      </Box>

      <Panel
        title="Stale devices"
        subtitle="Not revoked and not seen for over 3 days (oldest first, up to 10)"
        padded={false}
      >
        {h.sync.staleDevices.length === 0 ? (
          <EmptyState
            dense
            icon={<CheckCircleRounded />}
            title="Every device is checking in"
            body="No active device has been silent for more than 3 days."
          />
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Device</TableCell>
                  <TableCell>Shop</TableCell>
                  <TableCell align="right">Last seen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {h.sync.staleDevices.map((d) => (
                  <TableRow key={d.deviceId} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {d.deviceName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Link component={RouterLink} to={`/tenants/${d.tenantId}`} underline="hover">
                        {d.tenantName}
                      </Link>
                    </TableCell>
                    <TableCell align="right">
                      {d.lastSeenAt ? (
                        <Tooltip title={dateTime(d.lastSeenAt)}>
                          <Typography variant="body2" color="warning.main" component="span">
                            {relativeTime(d.lastSeenAt)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary" component="span">
                          Never
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Panel>
    </>
  );
}

const BANNER: Record<
  Overall,
  { tone: 'success' | 'warning' | 'error'; title: string; icon: ReactNode }
> = {
  HEALTHY: { tone: 'success', title: 'All systems normal', icon: <CheckCircleRounded /> },
  DEGRADED: { tone: 'warning', title: 'Needs attention', icon: <WarningRounded /> },
  DOWN: { tone: 'error', title: 'Service problem', icon: <ErrorRounded /> },
};

function OverallBanner({ status, reasons }: { status: Overall; reasons: string[] }) {
  const b = BANNER[status];
  return (
    <Card
      role="status"
      aria-label={`Overall status: ${b.title}`}
      sx={(t) => {
        const c = (t.vars ?? t).palette[b.tone].main;
        return {
          mb: 2.5,
          p: 2.25,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          borderColor: `color-mix(in srgb, ${c} 28%, transparent)`,
          background: `linear-gradient(100deg, color-mix(in srgb, ${c} 10%, transparent), transparent 70%)`,
        };
      }}
    >
      <IconBadge icon={b.icon} tone={b.tone} size={44} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h5" component="p">
          {b.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {reasons.length
            ? reasons.join(' ')
            : 'API, database and sync are all reporting normally.'}
        </Typography>
      </Box>
    </Card>
  );
}

function FieldGrid({ children }: { children: ReactNode }) {
  return <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>{children}</Box>;
}

function Mono({ children }: { children: ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize: '0.8125rem',
        wordBreak: 'break-all',
      }}
    >
      {children}
    </Box>
  );
}
